import { Button } from 'components';
import { LinearProgress } from 'components/LinearProgress';
import { FC, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { maskCurrencyFormat, maskDate } from 'util/masks';

import LeftIcon from '@mui/icons-material/ChevronLeft';
import RightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Collapse,
  Fab,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { IVoucher, IVoucherList } from 'types/Voucher';

interface IVoucherListProps extends IVoucherList {
  currentPage: number;
  setPage: Function;
  maxPage: number;
  loading: boolean;
}

interface IVoucherRow {
  voucher: IVoucher;
}

const VoucherRow: FC<IVoucherRow> = ({ voucher }) => {
  let history = useHistory();
  let { path } = useRouteMatch();
  const [open, setOpen] = useState(false);

  const handleEdit = () => {
    history.push(`${path}/edit`, {
      ...voucher,
    });
  };

  return (
    <>
      <TableRow
        key={voucher._id}
        sx={{
          '&:hover': { backgroundColor: '#ddd' },
        }}
      >
        <TableCell>{voucher?.description}</TableCell>
        <TableCell component="th" scope="row">
          {voucher?.name}
        </TableCell>

        <TableCell>{`${voucher.active ? 'Ativo' : 'Inativo'}`}</TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            paddingLeft: 0,
            paddingRight: 0,
            border: 0,
          }}
          colSpan={8}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table aria-label="more-info">
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="normal"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Grid container direction="row" columnSpacing={{ md: 6 }}>
                      <Grid
                        xs={2}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Typography>Desconto</Typography>
                        <strong>
                          {`${
                            voucher.type === 'percentage'
                              ? voucher.value + '%'
                              : maskCurrencyFormat(voucher.value)
                          }`}
                        </strong>
                      </Grid>

                      <Grid
                        xs={2}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Typography>Data de cadastro</Typography>
                        <strong>{maskDate(voucher.createdAt)}</strong>
                      </Grid>

                      <Grid
                        xs={2}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Typography>Data de expiração</Typography>
                        <strong>
                          {`${
                            voucher?.expireAt
                              ? maskDate(voucher?.expireAt)
                              : 'Sem data de expiração'
                          } `}
                        </strong>
                      </Grid>
                      <Grid
                        xs={2}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Typography>Limite de uso</Typography>
                        <strong>
                          {`${voucher?.quantity ? voucher?.quantity : ' - '} `}
                        </strong>
                      </Grid>
                    </Grid>
                    <Grid item style={{ width: 180 }}>
                      <Box py={1}>
                        <Button onClick={handleEdit}>Editar</Button>
                      </Box>
                    </Grid>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const VoucherList: FC<IVoucherListProps> = ({
  list,
  currentPage,
  setPage,
  maxPage,
  loading,
}) => {
  const previousPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < maxPage) {
      setPage(currentPage + 1);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [maxPage, setPage]);

  return (
    <TableContainer component={Paper} sx={{ mb: 5 }}>
      {loading && <LinearProgress />}
      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Descrição</strong>
            </TableCell>
            <TableCell>
              <strong>Código do cupom</strong>
            </TableCell>
            <TableCell>
              <strong>Status</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Ver mais</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list?.map((voucher, index) => (
            <VoucherRow key={index} voucher={voucher} />
          ))}
          <TableRow>
            <TableCell align="right" colSpan={12}>
              <Typography>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="remove"
                  disabled={currentPage <= 1}
                  onClick={previousPage}
                >
                  <LeftIcon />
                </Fab>
                <Fab
                  disabled
                  size="small"
                  aria-label="add"
                  sx={{ ml: 1, mr: 1 }}
                >
                  {currentPage}
                </Fab>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="add"
                  disabled={currentPage === maxPage}
                  onClick={nextPage}
                >
                  <RightIcon />
                </Fab>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

